import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { trackByKey } from '@ppl/utils/angular.utils';
import { FormValueControl } from '@ppl/utils/form.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';

@Component({
    selector: 'ppl-multi-checkbox',
    templateUrl: './multi-checkbox.component.html',
    styleUrls: ['./multi-checkbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PplMultiCheckboxComponent),
            multi: true
        }
    ],
    standalone: false
})
@FormValueControl()
export class PplMultiCheckboxComponent {
  @Input() value: string[] | null;
  @Input() options: MultiCheckboxOption[];
  @Input() initialValue?: string[] | null;
  @Input() hasChanges = false;
  @Input() @HostBinding('class.checkbox-margin') checkboxMargin = false;

  @Output() valueChange = new EventEmitter<string[]>();

  disabled = false;
  trackByValue = trackByKey<MultiCheckboxOption>('value');

  @MemoizeLast<PplMultiCheckboxComponent>(['options', 'value'])
  get normalizedOptions() {
    const value = this.value || [];
    return this.options.filter(option => !option.deleted || value.includes(option.value));
  }

  @MemoizeLast<PplMultiCheckboxComponent>(['value', 'hasChanges', 'initialValue', 'options'])
  get changes() {
    if (!this.hasChanges) {
      return [];
    } else {
      const initialValue = this.initialValue || [];

      const newItems = (this.value || []).filter(id => !initialValue.includes(id));
      const removedItems = (this.initialValue || []).filter(id => !this.value.includes(id));

      return [...newItems, ...removedItems];
    }
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  onCheck(selectedValue: string, checked: boolean) {
    if (checked) {
      this.valueChange.emit([...(this.value || []), selectedValue]);
    } else {
      this.valueChange.emit(this.value.filter(value => value !== selectedValue));
    }
  }
}

export interface MultiCheckboxOption {
  label: string;
  value: string;
  deleted?: boolean;
}
